/**
 * Questionnaire Approval Tab - טאב אישור שאלונים
 * Review and approve high-risk questionnaires (score >= 7)
 */

import { useEffect, useState } from 'react'
import { useAdminStore } from '../../store/adminStore'
import { RefreshCw, Eye, CheckCircle, XCircle, AlertTriangle } from 'lucide-react'
import type { PendingQuestionnaire } from '../../types'

export default function QuestionnaireApprovalTab() {
  const {
    pendingQuestionnaires,
    pendingQuestionnairesTotal,
    pendingQuestionnairesLoading,
    pendingQuestionnairesError,
    fetchPendingQuestionnaires,
    approveQuestionnaire,
    clearPendingQuestionnairesError
  } = useAdminStore()

  const [selectedQuestionnaire, setSelectedQuestionnaire] =
    useState<PendingQuestionnaire | null>(null)
  const [showApprovalModal, setShowApprovalModal] = useState(false)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [approvalAction, setApprovalAction] = useState<'approve' | 'reject'>('approve')
  const [notes, setNotes] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    fetchPendingQuestionnaires()
  }, [])

  const handleViewDetails = (questionnaire: PendingQuestionnaire) => {
    setSelectedQuestionnaire(questionnaire)
    setShowDetailModal(true)
  }

  const handleApprove = (questionnaire: PendingQuestionnaire, action: 'approve' | 'reject') => {
    setSelectedQuestionnaire(questionnaire)
    setApprovalAction(action)
    setNotes('')
    setShowApprovalModal(true)
  }

  const handleSubmitApproval = async () => {
    if (!selectedQuestionnaire) return

    if (notes.length < 10) {
      alert('נא להוסיף הערות מפורטות - לפחות 10 תווים')
      return
    }

    setSubmitting(true)
    try {
      await approveQuestionnaire(selectedQuestionnaire._id, {
        approved: approvalAction === 'approve',
        notes,
        reviewedBy: 'ישראל ישראלי'
      })
      setShowApprovalModal(false)
      setSelectedQuestionnaire(null)
      fetchPendingQuestionnaires()
    } catch (error) {
      console.error('Error approving questionnaire:', error)
    } finally {
      setSubmitting(false)
    }
  }

  const getRiskScoreBadge = (score: number) => {
    if (score >= 5) return <span className="badge badge-error text-lg">{score}</span>
    if (score >= 4) return <span className="badge badge-error">{score}</span>
    if (score >= 3) return <span className="badge badge-warning">{score}</span>
    return <span className="badge badge-info">{score}</span>
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-heading font-bold">אישור שאלוני סיכון</h2>
          <p className="text-gray-600 mt-1">
            שאלונים עם ציון סיכון גבוה (≥7) הדורשים אישור יועץ
          </p>
        </div>

        <button
          onClick={() => fetchPendingQuestionnaires()}
          disabled={pendingQuestionnairesLoading}
          className="btn btn-outline"
        >
          <RefreshCw
            size={18}
            className={pendingQuestionnairesLoading ? 'animate-spin' : ''}
            aria-hidden="true"
          />
          רענון
        </button>
      </div>

      {/* ISA Compliance Notice */}
      <div className="alert alert-warning">
        <AlertTriangle size={20} aria-hidden="true" />
        <div>
          <p className="font-medium">דרישת רשות ני״ע</p>
          <p className="text-sm mt-1">
            שאלונים עם ציון סיכון 7 ומעלה מחייבים אישור אישי של יועץ השקעות מורשה
            לפני מתן המלצות השקעה.
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card bg-yellow-50 border-yellow-200">
          <p className="text-sm text-gray-600 mb-1">שאלונים ממתינים</p>
          <p className="text-3xl font-bold text-yellow-700">
            {pendingQuestionnairesTotal}
          </p>
        </div>
        <div className="card bg-red-50 border-red-200">
          <p className="text-sm text-gray-600 mb-1">ציון סיכון גבוה (4-5)</p>
          <p className="text-3xl font-bold text-red-700">
            {pendingQuestionnaires.filter((q) => (q.normalizedScore ?? 0) >= 4).length}
          </p>
        </div>
        <div className="card bg-orange-50 border-orange-200">
          <p className="text-sm text-gray-600 mb-1">ציון סיכון בינוני (3)</p>
          <p className="text-3xl font-bold text-orange-700">
            {
              pendingQuestionnaires.filter(
                (q) => (q.normalizedScore ?? 0) === 3
              ).length
            }
          </p>
        </div>
      </div>

      {/* Error Alert */}
      {pendingQuestionnairesError && (
        <div className="alert alert-error" role="alert">
          <p>{pendingQuestionnairesError}</p>
          <button
            onClick={clearPendingQuestionnairesError}
            className="btn btn-sm btn-outline"
          >
            סגור
          </button>
        </div>
      )}

      {/* Questionnaires List */}
      {pendingQuestionnairesLoading ? (
        <div className="text-center py-12 text-gray-500">טוען שאלונים...</div>
      ) : pendingQuestionnaires.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <CheckCircle size={48} className="mx-auto text-green-500 mb-4" aria-hidden="true" />
          <p className="text-lg font-medium">אין שאלונים ממתינים לאישור</p>
          <p className="text-sm mt-2">כל השאלונים עם ציון גבוה אושרו</p>
        </div>
      ) : (
        <div className="space-y-4">
          {pendingQuestionnaires.map((questionnaire) => (
            <div
              key={questionnaire._id}
              className="card hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                {/* User Info */}
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold">
                      {questionnaire.userId.firstName}{' '}
                      {questionnaire.userId.lastName}
                    </h3>
                    {getRiskScoreBadge(questionnaire.normalizedScore ?? 0)}
                  </div>
                  <p className="text-sm text-gray-600 font-mono mb-2">
                    {questionnaire.userId.email}
                  </p>
                  <div className="flex gap-4 text-sm text-gray-600">
                    <span>
                      תאריך הגשה:{' '}
                      {questionnaire.completedAt &&
                        new Date(questionnaire.completedAt).toLocaleDateString(
                          'he-IL'
                        )}
                    </span>
                    <span>
                      גרסה: {questionnaire.version}
                    </span>
                    <span>
                      תשובות: {questionnaire.responses.length}
                    </span>
                  </div>

                  {/* Risk Profile */}
                  {questionnaire.riskProfile && (
                    <div className="mt-3 bg-gray-50 rounded p-3">
                      <p className="text-sm font-medium text-gray-700">
                        פרופיל סיכון: {questionnaire.riskProfile.levelHebrew}
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        {questionnaire.riskProfile.description}
                      </p>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex flex-col gap-2 mr-4">
                  <button
                    onClick={() => handleApprove(questionnaire, 'approve')}
                    className="btn btn-sm btn-success"
                  >
                    <CheckCircle size={16} aria-hidden="true" />
                    אישור
                  </button>
                  <button
                    onClick={() => handleApprove(questionnaire, 'reject')}
                    className="btn btn-sm btn-error"
                  >
                    <XCircle size={16} aria-hidden="true" />
                    דחייה
                  </button>
                  <button
                    onClick={() => handleViewDetails(questionnaire)}
                    className="btn btn-sm btn-outline"
                  >
                    <Eye size={16} aria-hidden="true" />
                    צפייה
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedQuestionnaire && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold">פרטי שאלון סיכון</h2>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-400 hover:text-gray-600"
                aria-label="סגירה"
              >
                ✕
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* User Info Card */}
              <div className="card bg-gray-50">
                <h3 className="font-bold text-lg mb-3">פרטי משתמש</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">שם מלא:</span>
                    <p className="font-medium">
                      {selectedQuestionnaire.userId.firstName}{' '}
                      {selectedQuestionnaire.userId.lastName}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-600">דוא"ל:</span>
                    <p className="font-medium font-mono">
                      {selectedQuestionnaire.userId.email}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-600">תאריך הגשה:</span>
                    <p className="font-medium">
                      {selectedQuestionnaire.completedAt &&
                        new Date(selectedQuestionnaire.completedAt).toLocaleDateString('he-IL', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-600">גרסת שאלון:</span>
                    <p className="font-medium">{selectedQuestionnaire.version}</p>
                  </div>
                </div>
              </div>

              {/* Risk Score Card */}
              <div className="card bg-gradient-to-br from-orange-50 to-red-50 border-orange-200">
                <h3 className="font-bold text-lg mb-3">ציון סיכון</h3>
                <div className="flex items-center gap-4">
                  <div className="text-5xl font-bold text-red-600">
                    {(selectedQuestionnaire.normalizedScore ?? 0).toFixed(1)}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-600">מתוך 5</p>
                    {selectedQuestionnaire.riskProfile && (
                      <>
                        <p className="font-bold text-lg mt-2">
                          {selectedQuestionnaire.riskProfile.levelHebrew}
                        </p>
                        <p className="text-sm text-gray-700 mt-1">
                          {selectedQuestionnaire.riskProfile.description}
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Responses */}
              <div className="card">
                <h3 className="font-bold text-lg mb-4">תשובות השאלון</h3>
                <div className="space-y-4">
                  {selectedQuestionnaire.responses.map((response, index) => (
                    <div
                      key={response.questionId}
                      className="border-b border-gray-200 pb-4 last:border-0"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <p className="font-medium text-gray-900">
                          {index + 1}. {response.question}
                        </p>
                        <span className="badge badge-info mr-2">
                          {response.score}/5
                        </span>
                      </div>
                      <p className="text-gray-700 bg-gray-50 p-3 rounded">
                        {response.answer}
                      </p>
                      {response.weight !== 1 && (
                        <p className="text-xs text-gray-500 mt-1">
                          משקל: {response.weight}x
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 justify-end border-t pt-4">
                <button
                  onClick={() => {
                    setShowDetailModal(false)
                    handleApprove(selectedQuestionnaire, 'reject')
                  }}
                  className="btn btn-error"
                >
                  <XCircle size={18} aria-hidden="true" />
                  דחייה
                </button>
                <button
                  onClick={() => {
                    setShowDetailModal(false)
                    handleApprove(selectedQuestionnaire, 'approve')
                  }}
                  className="btn btn-success"
                >
                  <CheckCircle size={18} aria-hidden="true" />
                  אישור
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Approval Modal */}
      {showApprovalModal && selectedQuestionnaire && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full">
            <div className="border-b px-6 py-4">
              <h2 className="text-xl font-bold">
                {approvalAction === 'approve' ? 'אישור שאלון סיכון' : 'דחיית שאלון סיכון'}
              </h2>
            </div>

            <div className="p-6 space-y-4">
              {/* Questionnaire Info */}
              <div className="bg-gray-50 p-4 rounded">
                <p className="font-medium mb-2">
                  {selectedQuestionnaire.userId.firstName}{' '}
                  {selectedQuestionnaire.userId.lastName}
                </p>
                <p className="text-sm text-gray-600">
                  ציון סיכון: {selectedQuestionnaire.normalizedScore ?? 0}/5
                </p>
                {selectedQuestionnaire.riskProfile && (
                  <p className="text-sm text-gray-600">
                    פרופיל: {selectedQuestionnaire.riskProfile.levelHebrew}
                  </p>
                )}
              </div>

              {/* Notes */}
              <div>
                <label htmlFor="questionnaire-approval-notes" className="block text-sm font-medium mb-2">
                  הערות יועץ <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="questionnaire-approval-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={4}
                  placeholder={
                    approvalAction === 'approve'
                      ? 'הסבר מדוע השאלון מאושר, המלצות נוספות...'
                      : 'הסבר מדוע השאלון נדחה, מה צריך לשנות...'
                  }
                  className="input"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  חובה להוסיף הערות מפורטות - לפחות 10 תווים
                </p>
              </div>

              {approvalAction === 'approve' && (
                <div className="alert alert-info text-sm">
                  <p>
                    לאחר האישור, המשתמש יקבל אימייל עם פרטי הפרופיל שלו והמלצות
                    השקעה מותאמות.
                  </p>
                </div>
              )}

              {approvalAction === 'reject' && (
                <div className="alert alert-warning text-sm">
                  <p>
                    לאחר הדחייה, המשתמש יקבל אימייל עם הסבר והנחיות למילוי שאלון
                    מחדש.
                  </p>
                </div>
              )}
            </div>

            <div className="border-t px-6 py-4 flex gap-3 justify-end">
              <button
                onClick={() => setShowApprovalModal(false)}
                disabled={submitting}
                className="btn btn-outline"
              >
                ביטול
              </button>
              <button
                onClick={handleSubmitApproval}
                disabled={submitting}
                className={`btn ${
                  approvalAction === 'approve' ? 'btn-success' : 'btn-error'
                }`}
              >
                {submitting
                  ? 'שולח...'
                  : approvalAction === 'approve'
                  ? 'אשר שאלון'
                  : 'דחה שאלון'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
